import {
	toDataSourceRequestString,
	translateDataSourceResultGroups,
	DataSourceRequestState
} from '@progress/kendo-data-query';
import AddressHistory from '../models/be/AddressHistory';
import AppApi from './AppApi';
import AuthenticationResult, { RestToken } from '../models/be/AuthenticationResult';
import CheckType from '../models/CheckType';
import User from '../models/be/coreApi/User';
import { apiRootUrl, countryIsUk } from '../config.local';
import { createQueryString, hasValue } from '../lib/utils';
import Candidate, { PaymentModel } from '../models/be/Candidate';
import WorkflowTemplate from '../models/be/WorkflowTemplate';
import WorkflowCreation from '../models/be/WorkflowCreation';
import {
	getAccessToken,
	hasCustomerRole,
	hasCandidateRole,
	hasSystemAdminRole,
	isLoggedIn,
	hasCustomerSuccessRole
} from './session';
import { RefereeAnswer } from '../models/be/RefereeAnswer';
import ReferenceTemplate from '../models/be/ReferenceTemplate';
import { SortDirection } from './enums';
import { statuses } from '../lib/ApiBase';
import PrimaryName from '../models/be/PrimaryName';
import Workflow, { BillingDetails, InternationalChecks, LatestWorkflow } from '../models/be/Workflow';
import Reference from '../models/be/Reference';
import CandidateActionWorkflow from '../models/be/CandidateActionWorkflow';
import CandidateForm from '../models/be/transport/CandidateForm';
import Document from '../models/be/db/Document';
import FormSpecification, { Section } from '../models/be/db/FormSpecification';
import Event from '../models/be/Event';
import EventLog from '../models/be/EventLog';
import Package from '../models/be/Package';
import CapturedReferenceDetails from '../models/be/CapturedReferenceDetails';
import ChangeLog from '../models/be/ChangeLog';
import { Session } from '../models/be/transport/Session';
import { PagedDocuments } from '../models/be/transport/PagedDocuments';
import AggregatedCandidates from '../models/be/transport/AggregatedCandidates';
import FilterSettings from '../models/be/FilterSettings';
import FraudFlag from '../models/be/FraudFlag';
import { ReminderTemplate } from '../models/be/ReminderTemplate';
import AdditionalName from '../models/be/AdditionalName';
import CustomWorkflowTemplate from '../models/be/db/CustomWorkflowTemplate';
import ReportWorkflows from '../models/be/ReportWorkflows';
import ConsentSignature from '../models/be/ConsentSignature';
import { auCountryCode, notificationType, ukCountryCode } from './constants';
import { RefereeWorkflow } from '../models/be/RefereeWorkflow';
import { JobAdderActionDetail, JobAdderApplication, JobAdderCandidate } from '../models/be/JobAdderResults';
import { UserLoginAttemptsDetails } from '../models/be/transport/UserLoginAttemptsDetails';
import { ConnectIdAuthResponse } from '../models/be/transport/ConnectIdAuthResponse';
import { ConnectIdTokenRequestModel } from '../models/be/transport/connectIdTokenRequestModel';
import { Registration } from '../models/be/Customer';
import FileItem from '../models/be/coreApi/FileItem';

// TODO: Consider splitting up this file!

export const authenticate = async (
	email: string,
	password: string,
	remembered: boolean
): Promise<AuthenticationResult> => {
	const url = `${apiRootUrl}/api/Authentication`;
	const response = await fetch(url, { method: 'POST', body: JSON.stringify({ email, password, remembered }) });

	if (response.status === statuses.ok) {
		return response.json();
	}
	if (response.status === statuses.unauthorized || response.status === statuses.badRequest) {
		return { errorMessage: await response.text() };
	}
	throw new Error('Unexpected status code from Authentication endpoint.');
};

export const validateCaptchaValue = async (captchaValue: string): Promise<{ errorMessage: string }> => {
	const queryString = createQueryString({ captchaValue });
	const url = `${apiRootUrl}/api/Authentication/validateCaptcha${queryString}`;
	const response = await fetch(url, { method: 'POST', body: null });

	if (response.status === statuses.ok) {
		return null;
	}

	if (response.status === statuses.unauthorized || response.status === statuses.badRequest) {
		return { errorMessage: await response.text() };
	}
	throw new Error('Unexpected status code from Authentication endpoint.');
};

const appApi = new AppApi();

export const generateCandidateTokenForAdmin = async (candidateId: string): Promise<string> => {
	const queryString = createQueryString({ candidateId });
	return appApi.ajax('post', `/api/Authentication/Masquerade${queryString}`);
};

export const generateUserMasqueradeTokenForSystemAdmin = async (
	email: string,
	subCustomerId: number
): Promise<AuthenticationResult> => {
	const queryString = createQueryString({ username: email, subCustomerId });
	return appApi.ajax('post', `/api/Authentication/Masquerade${queryString}`);
};

export const acquireRefereeTokenForSystemAdmin = async (
	workflowId: string,
	clientCustomerId: number
): Promise<string> => {
	const queryString = createQueryString({ workflowId, clientCustomerId });
	return appApi.ajax('post', `/api/Authentication/Masquerade${queryString}`);
};

export const getAuthCode = async (userName: string, password: string): Promise<string> => {
	const queryString = createQueryString({ userName, password });
	return appApi.ajax('get', `/api/Authentication/authcode${queryString}`);
};

export const GetJobAdderAccessTokenFromAuthCode = async (authCode: string, redirectUrl: string): Promise<RestToken> => {
	const data = { authCode, redirectUrl };
	return appApi.ajax('post', '/api/Authentication/jobAdderAccessTokenFromAuthCode', data, null).then(
		async (value) => {
			return value;
		},
		async (err) => {
			return { errorMessage: err.body };
		}
	);
};

export const getJobAdderCandidate = async (
	candidateId: string,
	integrationId: number,
	customerId: number
): Promise<JobAdderCandidate> => {
	const queryString = createQueryString({ candidateId, integrationId, customerId });
	return appApi.ajax('get', `/api/JobAdder/candidate${queryString}`);
};

export const getJobAdderApplication = async (
	applicationId: string,
	integrationId: number,
	customerId: number
): Promise<JobAdderApplication> => {
	const queryString = createQueryString({ applicationId, integrationId, customerId });
	return appApi.ajax('get', `/api/JobAdder/application${queryString}`);
};

export const getJobAdderActionDetails = async (customerId: number): Promise<JobAdderActionDetail[]> => {
	const queryString = createQueryString({ customerId });
	return appApi.ajax('get', `/api/JobAdder/actionDetail${queryString}`);
};

export const createJobAdderActionDetails = async (
	customerId: number,
	actionDetails: JobAdderActionDetail[]
): Promise<unknown> => {
	const queryString = createQueryString({ customerId });
	return appApi.ajax('post', `/api/JobAdder/actionDetail${queryString}`, actionDetails);
};

export const getCandidates = async (
	textSearch: string,
	refereeTextSearch: string,
	candidateFilters: string[],
	workflowFilters: string[],
	workflowTemplateIds: string[],
	ownerUserIds: number[],
	recentActivityFromDate: string,
	recentActivityToDate: string,
	dateFilter: string,
	sortBy: string,
	sortDirection: SortDirection,
	pageNumber: number,
	signal: AbortSignal = null,
	customerId: number = null,
	total: number = null
): Promise<AggregatedCandidates> => {
	const qs = createQueryString({
		textSearch,
		refereeTextSearch,
		candidateFilters,
		workflowFilters,
		workflowTemplateIds,
		ownerUserIds,
		recentActivityFromDate,
		recentActivityToDate,
		dateFilter,
		sortBy,
		sortDirection,
		customerId,
		pageNumber,
		total
	});
	return appApi.ajax('get', `/api/Candidates${qs}`, null, signal);
};

export const getReferenceTemplates = async (subCustomerId: number): Promise<ReferenceTemplate[]> => {
	const qs = createQueryString({ subCustomerId });
	return appApi.ajax('get', `/api/ReferenceTemplates${qs}`, null);
};

export const getReferenceTemplate = async (id: string, clientCustomerId: number): Promise<ReferenceTemplate> => {
	const qs = createQueryString({ clientCustomerId });
	return appApi.ajax('get', `/api/ReferenceTemplates/${id}${qs}`, null);
};

export const createReferenceTemplate = async (referenceTemplate: ReferenceTemplate): Promise<ReferenceTemplate> => {
	return appApi.ajax('POST', `/api/ReferenceTemplates/`, referenceTemplate);
};

export const updateReferenceTemplate = async (referenceTemplate: ReferenceTemplate): Promise<ReferenceTemplate> => {
	return appApi.ajax('POST', `/api/ReferenceTemplates/${referenceTemplate.id}`, referenceTemplate);
};

export const createCandidate = async (candidateForm: CandidateForm): Promise<string> => {
	return appApi.ajax('post', '/api/Candidates?action=createCandidate', candidateForm);
};

export const createMultipleCandidates = async (candidateForm: CandidateForm): Promise<string> => {
	return appApi.ajax(
		'post',
		`/api/Candidates?action=createMultipleCandidates&isUKCustomer=${countryIsUk}`,
		candidateForm
	);
};

export const requestCandidateDataDeletion = async (candidateId: string): Promise<string> => {
	return appApi.ajax('post', `/api/Candidates/${candidateId}/DataRequests?action=requestDataDeletion`);
};

export const requestCandidateData = async (candidateId: string): Promise<string> => {
	return appApi.ajax('post', `/api/Candidates/${candidateId}/DataRequests?action=requestData`);
};

export const deleteCandidate = async (candidateId: string): Promise<void> =>
	appApi.ajax('delete', `/api/Candidates/${candidateId}`);

export const scrubCandidate = async (candidateId: string): Promise<void> =>
	appApi.ajax('post', `/api/Candidates/${candidateId}/scrub`);

export const deleteWorkflow = async (workflowId: string, clientCustomerId: number): Promise<void> => {
	const queryString = createQueryString({ clientCustomerId });
	return appApi.ajax('delete', `/api/Workflows/${workflowId}/logs${queryString}`);
};

export const getWorkflowTemplates = async (
	subCustomerId: number,
	signal?: AbortSignal
): Promise<WorkflowTemplate[]> => {
	const queryString = createQueryString({ subCustomerId });
	const workflowTemplates: WorkflowTemplate[] = await appApi.ajax(
		'get',
		`/api/WorkflowTemplates${queryString}`,
		null,
		signal
	);
	workflowTemplates.forEach((t) => {
		if (t.initialisationSection !== null) {
			setSectionComponentIdsToBeUsedAsReactKeys(t.initialisationSection);
		}
	});
	return workflowTemplates;
};

export const getWorkflow = async (id: string, signal?: AbortSignal): Promise<Workflow> =>
	appApi.ajax('get', `/api/Workflows/${id}`, null, signal);

export const updateWorkflow = async (id: string, signal?: AbortSignal): Promise<Workflow> =>
	appApi.ajax('post', `/api/Workflows/${id}`, null, signal);

export const getWorkflows = async (
	selectedCustomerId: number,
	includeOutcome: boolean,
	timeperiod: string,
	groupBy: string,
	checkStatus?: string,
	checkType?: string,
	signal?: AbortSignal
): Promise<ReportWorkflows[]> => {
	const timezoneOffsetWithUtcToLocalPolarity = new Date().getTimezoneOffset() * -1;
	const data = appApi.ajax(
		'get',
		`/api/AllWorkflows?selectedCustomerId=${selectedCustomerId}&includeOutcome=${includeOutcome}
		&tzOffsetMinutes=${timezoneOffsetWithUtcToLocalPolarity}&checkType=${checkType}&checkStatus=${checkStatus}
		&timeperiod=${timeperiod}&groupBy=${groupBy}`,
		null,
		signal
	);
	return data;
};

export const getLatestWorkflow = async (selectedCustomerId: number, signal?: AbortSignal): Promise<LatestWorkflow[]> =>
	appApi.ajax('get', `/api/LatestWorkflow?selectedCustomerId=${selectedCustomerId}`, null, signal);

export const generateNewRefereeToken = async (refereeToken: string, clientCustomerId: number): Promise<string> => {
	const queryString = createQueryString({ action: 'authenticate', clientCustomerId });
	return appApi.ajax(
		'post',
		`/api/referees/${refereeToken}${queryString}`,
		null,
		null,
		false,
		null,
		getAuthorizationHeaderForSystemAdminMasqueradingAsReferee(refereeToken)
	);
};

export const sendNewLinkToReferee = async (expiredRefereeToken: string, pagePath: string) => {
	const queryString = createQueryString({ action: 'sendNewLinkToReferee', pagePath });
	return appApi.ajax('post', `/api/referees/${expiredRefereeToken}${queryString}`, null, null, false);
};

export const getReferenceFormSpecification = async (
	refereeToken: string = null,
	signal: AbortSignal = null
): Promise<Section[]> => {
	const queryString = createQueryString({ action: 'isUKCheck', countryIsUk });
	const formSections = appApi.ajax(
		'get',
		`/api/ReferenceFormSpecification/${refereeToken}${queryString}`,
		null,
		signal,
		refereeToken === null
	);
	return formSections;
};

export const getFormSpecification = async (
	workflowId: string,
	activityId: string,
	signal?: AbortSignal
): Promise<FormSpecification> =>
	appApi.ajax(
		'get',
		`/api/Workflows/${workflowId}/FormSpecification${createQueryString({ activityId })}`,
		null,
		signal
	);

export const getParentAndChildWorkflowsForCustomer = async (id: string, signal?: AbortSignal): Promise<Workflow[]> =>
	appApi.ajax('get', `/api/Workflows/ParentAndChildren/${id}`, null, signal);

export const getWorkflowsForCandidate = async (
	candidateId: string,
	signal?: AbortSignal,
	excludeArchived = true
): Promise<Workflow[]> =>
	appApi.ajax('get', `/api/Workflows${createQueryString({ candidateId, excludeArchived })}`, null, signal);

export const getPartialWorkflowDetailsByWorkflowIdForDataCapture = async (): Promise<Workflow> => {
	throw new Error('Remove this fn.');
};

export const addChecks = async (candidateId: string, workflowCreations: WorkflowCreation[]): Promise<any> => {
	return appApi.ajax(
		'post',
		`/api/Candidates/${candidateId}/Workflows${createQueryString({ notificationType })}`,
		workflowCreations
	);
};

export const addChecksToMultipleCandidates = async (
	candidateIds: string[],
	workflowCreations: WorkflowCreation[]
): Promise<string> => {
	return appApi.ajax('post', `/api/Candidates/Workflows${createQueryString({ notificationType })}`, {
		candidateIds,
		workflowCreations
	});
};

export const changeCandidateSubCustomer = async (candidateId: string, subCustomerId: number): Promise<Candidate> => {
	return appApi.ajax('post', `/api/candidates/${candidateId}/${createQueryString({ action: 'changeSubCustomer' })}`, {
		subCustomerId
	});
};

export const cancelCandidate = async (candidateId: string): Promise<Candidate> => {
	return appApi.ajax('post', `/api/candidates/${candidateId}/${createQueryString({ action: 'cancelCandidate' })}`);
};

export const changeCandidateOwner = async (candidateId: string, ownerUserId: number): Promise<Candidate> => {
	return appApi.ajax(
		'post',
		`/api/candidates/${candidateId}/${createQueryString({ action: 'changeCandidateOwner' })}`,
		{ ownerUserId }
	);
};

export const actOnWorkflow = async (
	workflowId: string,
	clientCustomerId: number,
	action: string,
	activityId: string = null
): Promise<Workflow> => {
	const queryString = createQueryString({ action, clientCustomerId, activityId });
	return appApi.ajax('post', `/api/Candidates/Workflows/${workflowId}${queryString}`);
};

export const GetPaymentCost = async (
	workflowTypeCode: string,
	customerId: number
): Promise<{ amount: number; currencyIsoCode: string }> => {
	return appApi.ajax('get', `/api/Customer/${customerId}/Payment/${workflowTypeCode}`);
};

export const partialUpdateWorkflow = async (
	workflowId: string,
	activityId: string,
	partialFields: Partial<Workflow>
): Promise<Workflow> => {
	const countryCode = countryIsUk ? ukCountryCode : auCountryCode;
	return appApi.ajax(
		'PATCH',
		`/api/Workflows/${workflowId}${createQueryString({ activityId, countryCode })}`,
		partialFields
	);
};

export const addReferenceToParentWorkflow = async (workflowId: string, reference: Reference): Promise<Workflow> => {
	return appApi.ajax('put', `/api/Workflows/${workflowId}/References`, reference);
};

export const updateReference = async (parentWorkflowId: string, reference: Reference): Promise<Workflow> => {
	return appApi.ajax(
		'post',
		`/api/Workflows/${parentWorkflowId}/References${createQueryString({
			action: 'updateReference'
		})}`,
		reference
	);
};

export const CreateRefChildWorkflowsIfNotExists = async (
	parentWorkflowId: string,
	reference: Reference
): Promise<Workflow> => {
	return appApi.ajax(
		'post',
		`/api/Workflows/${parentWorkflowId}/References${createQueryString({
			action: 'createChildReferenceWorkflow'
		})}`,
		reference
	);
};

export const updateReferenceOfChildWorkflow = async (
	workflowId: string,
	capturedReferenceDetails: CapturedReferenceDetails
): Promise<Workflow> => {
	return appApi.ajax('post', `/api/Workflows/${workflowId}/Reference`, capturedReferenceDetails);
};

export const dismissGapsInReferences = async (parentWorkflowId: string, gapsDismissed: boolean): Promise<Workflow> => {
	return appApi.ajax(
		'post',
		`/api/Workflows/${parentWorkflowId}/References${createQueryString({
			action: 'dismissGapsInReferences',
			gapsDismissed
		})}`
	);
};

export const askCandidateToFillGaps = async (parentWorkflowId: string): Promise<string> => {
	return appApi.ajax(
		'post',
		`/api/Workflows/${parentWorkflowId}/References${createQueryString({
			action: 'askCandidateToFillGaps'
		})}`
	);
};

export const deleteReferenceOfParentWorkflow = async (workflowId: string, referenceId: string): Promise<Workflow> => {
	return appApi.ajax('delete', `/api/Workflows/${workflowId}/References/${referenceId}`);
};

export const updateCandidateNotesConsent = async (workflowId: string, consent: boolean): Promise<Workflow> => {
	const queryString = createQueryString({ consent });
	return appApi.ajax('put', `/api/Workflows/${workflowId}/References/Notes${queryString}`);
};

export const manuallyRemindRefereeToCompleteReference = async (childWorkflowId: string): Promise<Workflow> => {
	const queryString = createQueryString({ action: 'manuallyRemind' });
	return appApi.ajax('post', `/api/Workflows/References/${childWorkflowId}${queryString}`);
};

export const pauseReferenceAutoReminders = async (childWorkflowId: string): Promise<Workflow> => {
	const queryString = createQueryString({ action: 'pauseAutoReminders' });
	return appApi.ajax('post', `/api/Workflows/References/${childWorkflowId}${queryString}`);
};

export const resumeReferenceAutoReminders = async (childWorkflowId: string): Promise<Workflow> => {
	const queryString = createQueryString({ action: 'resumeAutoReminders' });
	return appApi.ajax('post', `/api/Workflows/References/${childWorkflowId}${queryString}`);
};

export const resetReferenceAutoReminders = async (childWorkflowId: string): Promise<Workflow> => {
	const queryString = createQueryString({ action: 'resetAutoReminders' });
	return appApi.ajax('post', `/api/Workflows/References/${childWorkflowId}${queryString}`);
};

export const submitCandidateReview = async (
	workflowId: string,
	candidateFieldsToReview: Record<string, string[]>,
	notesToCandidate: string,
	clientCustomerId: number
): Promise<Workflow> => {
	const body = { correctionSectionsAndFieldsList: candidateFieldsToReview, notesToCandidate, clientCustomerId };
	return appApi.ajax('POST', `/api/Workflows/${workflowId}/CandidateReviewRequest`, body);
};

export const partialUpdateCandidate = async (
	candidateId: string,
	partialFields: Partial<Candidate>
): Promise<Candidate> => {
	return await appApi.ajax('PATCH', `/api/Candidates/${candidateId}`, partialFields);
};

export const updateCandidate = async (id: string, candidate: Candidate): Promise<void> => {
	let queryParam = '';
	if (hasCustomerRole()) {
		queryParam = `&candidateId=${id}`;
	} else if (hasCandidateRole()) {
		queryParam = '&isCandidate=1';
	}
	const queryString = `?action=updateCandidate${queryParam}`;
	await appApi.ajax('post', `/api/Candidates${queryString}`, candidate);
};

export const getCandidatePrimaryName = async (candidateId?: string): Promise<PrimaryName> => {
	return sendRequestByWorkflowIdOrCandidateId(candidateId, 'get', '/api/Candidates/PrimaryName');
};

export const getCandidateIpAddress = async (signal?: AbortSignal): Promise<string> => {
	return appApi.ajax('get', `/api/Candidates/IpAddress`, null, signal);
};

export const getCandidateAddressHistories = async (
	workflowId: string,
	candidateId?: string,
	signal?: AbortSignal
): Promise<AddressHistory[]> => {
	const queryString = createQueryString({ workflowId, candidateId });
	return appApi.ajax('get', `/api/Candidates/AddressHistories${queryString}`, null, signal);
};

export const getFile = async (fileId: string, refereeToken: string = null): Promise<string> => {
	const queryString = createQueryString({ fileId, refereeToken });
	return appApi.ajax('get', `/api/Files/RequestId${queryString}`, null, null, !hasValue(refereeToken));
};

export const uploadFile = async (queryString: string, blob: FormData): Promise<string> => {
	return appApi.ajax('post', `/api/Files/RequestId${queryString}`, blob);
};

export const appendAttachment = async (
	workflowId: string,
	clientCustomerId: number,
	formData: FormData
): Promise<Workflow> => {
	return appApi.ajax(
		'post',
		`/api/Workflows/${workflowId}/Attachments/${createQueryString({ clientCustomerId })}`,
		formData,
		null,
		true,
		null,
		null,
		true
	);
};

export const verifyUkviShareCode = async (
	workflowId: string,
	clientCustomerId: number,
	formData: FormData
): Promise<Workflow> => {
	return appApi.ajax(
		'post',
		`/api/Workflows/${workflowId}/VerifyUkviShareCode${createQueryString({ clientCustomerId })}`,
		formData,
		null,
		true,
		null,
		null,
		true
	);
};

export const overrideResult = async (workflowId: string): Promise<Workflow> => {
	return appApi.ajax('put', `/api/Workflows/${workflowId}/Override`);
};

export const offlineVerifyCheckResult = async (workflowId: string): Promise<Workflow> => {
	return appApi.ajax('put', `/api/Workflows/${workflowId}/offlineVerifyCheckResult`);
};

export const addCloudCheckActivityToWorkflow = async (workflowId: string): Promise<Workflow> => {
	return appApi.ajax('put', `/api/Workflows/${workflowId}/AddCloudCheckActivity`);
};

export const deleteAttachment = async (
	workflowId: string,
	clientCustomerId: number,
	attachmentId: string
): Promise<Workflow> => {
	return appApi.ajax(
		'delete',
		`/api/Workflows/${workflowId}/Attachments/${attachmentId}${createQueryString({ clientCustomerId })}`
	);
};

export const appendComment = async (
	workflowId: string,
	sendNotification: boolean,
	sendNotificationToRecipient: boolean,
	comment: { value: string }
): Promise<Workflow> => {
	return appApi.ajax(
		'post',
		`/api/Workflows/${workflowId}/Comments${createQueryString({ sendNotification, sendNotificationToRecipient })}`,
		comment
	);
};

export const deleteComment = async (
	workflowId: string,
	clientCustomerId: number,
	commentId: string
): Promise<Workflow> => {
	return appApi.ajax(
		'delete',
		`/api/Workflows/${workflowId}/Comments/${commentId}${createQueryString({ clientCustomerId })}`
	);
};

export const sendForgottenPasswordEmail = async (emailAddress: string, isNewUser?: boolean): Promise<string> => {
	return appApi.ajax(
		'post',
		`/api/Authentication/Password?action=sendForgottenPasswordEmail&isNewUser=${isNewUser}`,
		emailAddress,
		null,
		false
	);
};

export const sendForgottenPasswordLink = async (emailAddress: string, isNewUser?: boolean): Promise<string> => {
	return appApi.ajax(
		'post',
		`/api/Authentication/Password?action=forgotPasswordLink&isNewUser=${isNewUser}`,
		emailAddress,
		null,
		false
	);
};

export const setPassword = async (secret: string, newPassword: string, workflowToken?: string): Promise<void> => {
	const body = { secret, newPassword, workflowToken };
	return appApi.ajax('post', '/api/Authentication/Password', body, null, false);
};

export const updatePassword = async (oldPassword: string, newPassword: string): Promise<void> => {
	const body = { oldPassword, newPassword };
	return appApi.ajax('post', '/api/Authentication/Password?action=updatePassword', body, null);
};

export const getCheckTypes = async (signal: AbortSignal = null, id = 0, pageName = ''): Promise<CheckType[]> => {
	const url = `/api/Workflowchecktypes?customerId=${id}${pageName ? `&pageName=${pageName}` : ''}`;
	return await appApi.ajax('get', url, null, signal);
};

export const getCheckPricing = async (signal: AbortSignal = null, id = 0): Promise<CheckType[]> => {
	const url = `/api/CheckPricing?customerId=${id}`;
	return await appApi.ajax('get', url, null, signal);
};

export const getCustomerUsersAndAbove = async (
	clientCustomerId: number,
	subCustomerId: number,
	signal: AbortSignal = null
): Promise<User[]> => {
	return appApi.ajax('get', `/api/Users/Customer/${clientCustomerId}/SubCustomer/${subCustomerId}`, null, signal);
};

export const getUserLoginAttempts = async (
	email: string,
	signal: AbortSignal = null
): Promise<UserLoginAttemptsDetails> => {
	return appApi.ajax('get', `/api/Users/${email}/loginAttempts`, null, signal, false);
};

export const getReferenceFromRefereeToken = async (
	refereeToken: string,
	signal: AbortSignal = null
): Promise<RefereeWorkflow> => appApi.ajax('get', `/api/referees/${refereeToken}`, null, signal, false);

export const getCandidateFromRefereeToken = async (
	refereeToken: string,
	signal: AbortSignal = null
): Promise<PrimaryName> => {
	const queryString = createQueryString({ refereeToken });
	return appApi.ajax('get', `/api/candidates/candidate${queryString}`, null, signal, false);
};

export const postRefereeAnswers = async (refereeToken: string, answers: RefereeAnswer[]) => {
	return appApi.ajax(
		'post',
		`/api/referees/questions/${refereeToken}`,
		answers,
		null,
		false,
		null,
		getAuthorizationHeaderForSystemAdminMasqueradingAsReferee(refereeToken)
	);
};

export const setRefereeConsent = async (refereeToken: string, consent: boolean) => {
	const queryString = createQueryString({ consent });
	return appApi.ajax(
		'put',
		`/api/referees/consent/${refereeToken}${queryString}`,
		null,
		null,
		false,
		null,
		getAuthorizationHeaderForSystemAdminMasqueradingAsReferee(refereeToken)
	);
};

export const setRefereeNotesConsent = async (refereeToken: string, consent: boolean) => {
	const queryString = createQueryString({ consent });
	return appApi.ajax(
		'put',
		`/api/referees/notes/consent/${refereeToken}${queryString}`,
		null,
		null,
		false,
		null,
		getAuthorizationHeaderForSystemAdminMasqueradingAsReferee(refereeToken)
	);
};

export const updateCapturedReferenceDetailsAsReferee = async (
	refereeToken: string,
	capturedReferenceDetails: CapturedReferenceDetails
) => {
	return appApi.ajax(
		'put',
		`/api/referees/capturedReferenceDetails/${refereeToken}`,
		capturedReferenceDetails,
		null,
		false,
		null,
		getAuthorizationHeaderForSystemAdminMasqueradingAsReferee(refereeToken)
	);
};

export const saveReferenceForLater = async (refereeToken: string, pagePath: string) => {
	return appApi.ajax(
		'post',
		`/api/referees/${refereeToken}${createQueryString({ action: 'saveForLater', pagePath })}`,
		null,
		null,
		false,
		null,
		getAuthorizationHeaderForSystemAdminMasqueradingAsReferee(refereeToken)
	);
};

export const completeReferenceAsReferee = async (
	refereeToken: string,
	completeWithQuestions: boolean,
	session: Session,
	onlyRefereeConfirmation: boolean
) => {
	return appApi.ajax(
		'post',
		`/api/referees/${refereeToken}${createQueryString({
			action: 'complete',
			completeWithQuestions,
			onlyRefereeConfirmation
		})}`,
		session,
		null,
		false,
		null,
		getAuthorizationHeaderForSystemAdminMasqueradingAsReferee(refereeToken)
	);
};

export const submitReferences = async (workflowId: string, session: Session): Promise<string> => {
	const queryString = createQueryString({ workflowId });
	return appApi.ajax('post', `/api/Workflows/References${queryString}`, session);
};

export const setCandidateConsent = async (overallConsent: boolean, workflowId: string): Promise<void> => {
	const queryString = createQueryString({ workflowId });
	return appApi.ajax('put', `/api/Workflows/CandidateConsent${queryString}`, overallConsent);
};

export const setCandidateSignature = async (consentSignature: ConsentSignature, workflowId: string): Promise<void> => {
	const queryString = createQueryString({ workflowId });
	return appApi.ajax('post', `/api/Workflows/CandidateConsentSignature${queryString}`, consentSignature);
};

export const completeDataCaptureActivity = async (
	workflowId: string,
	activityId: string,
	customerName: string,
	action = ''
): Promise<Workflow> => {
	const queryString = createQueryString({ workflowId, activityId, action, customerName });
	return appApi.ajax('post', `/api/Workflows/Activities/${queryString}`);
};

// TODO: This is being used for DS. Should it? If so, needs to be renamed.
export const putCustomerUserAgreementForDbsCheck = async (workflowId: string, idName: string): Promise<void> => {
	const body = idName;
	return appApi.ajax('put', `/api/Workflows/${workflowId}/CustomerUserAgreement`, body);
};

export const getCandidateActionWorkflows = async (signal: AbortSignal): Promise<CandidateActionWorkflow[]> => {
	return appApi.ajax('get', '/api/Candidates/Workflows', null, signal);
};

export const sendEvent = async <T>(
	event: Event,
	queryString = '',
	timeoutInMilliseconds: number = null
): Promise<T> => {
	const countryCode = countryIsUk ? ukCountryCode : auCountryCode;
	const updatedQueryString = hasValue(queryString)
		? `${queryString}&countryCode=${countryCode}`
		: createQueryString({ countryCode });
	return appApi.ajax('post', `/api/Events${updatedQueryString}`, event, null, isLoggedIn(), timeoutInMilliseconds);
};

export const getEventLogs = async (
	continuationToken: string,
	roleSearch: string,
	sortBy: string,
	sortDirection: SortDirection,
	workflowId: string,
	signal: AbortSignal = null
): Promise<PagedDocuments<EventLog>> => {
	const qs = createQueryString({
		continuationToken,
		roleSearch,
		sortBy,
		sortDirection,
		workflowId
	});
	return appApi.ajax('get', `/api/EventLogs${qs}`, null, signal);
};

export const setDocument = async (
	workflowId: string,
	candidateId: string,
	clientCustomerId: number,
	refereeToken: string,
	fieldName: string,
	document: Document
): Promise<void> => {
	return appApi.ajax(
		'post',
		`/api/Documents${createQueryString({ workflowId, candidateId, refereeToken, clientCustomerId })}`,
		{ fieldName, document },
		null,
		!hasValue(refereeToken),
		null,
		getAuthorizationHeaderForSystemAdminMasqueradingAsReferee(refereeToken)
	);
};

export const getReportDataMi = async (
	dataState: DataSourceRequestState,
	selectedCustomerId: number,
	includeBranches: boolean,
	includeArchived: boolean,
	includeReferees: boolean
) => {
	const timezoneOffsetWithUtcToLocalPolarity = new Date().getTimezoneOffset() * -1;
	const data = await appApi.ajax(
		'get',
		`/api/Reports/Mi?${toDataSourceRequestString(
			dataState
		)}&selectedCustomerId=${selectedCustomerId}&tzOffsetMinutes=${timezoneOffsetWithUtcToLocalPolarity}
		  &includeBranches=${includeBranches}&includeArchived=${includeArchived}&includeReferees=${includeReferees}`,
		null,
		null,
		true
	);
	return dataState.group && dataState.group.length ? translateDataSourceResultGroups(data) : data;
};

export const getPackagesForSelectedCustomer = async (
	clientCustomerId: number,
	subCustomerId: number,
	includeArchived = false,
	excludeSubPackages = false,
	signal?: AbortSignal
): Promise<Package[]> => {
	if (!hasValue(subCustomerId)) {
		throw new Error('subCustomerId is not a valid value.');
	}
	const queryString = createQueryString({ clientCustomerId, subCustomerId, includeArchived, excludeSubPackages });
	return appApi.ajax('get', `/api/Packages${queryString}`, null, signal);
};

export const createPackageForSelectedCustomer = async (
	packageToCreate: Package,
	subCustomerId: number,
	signal: AbortSignal
): Promise<string> => {
	const queryString = createQueryString({ subCustomerId });
	return appApi.ajax('post', `/api/Packages${queryString}`, packageToCreate, signal);
};

export const getPackageForSelectedCustomer = async (
	id: string,
	subCustomerId: number,
	signal: AbortSignal
): Promise<Package> => {
	const queryString = createQueryString({ subCustomerId });
	return appApi.ajax('get', `/api/Packages/${id}${queryString}`, null, signal);
};

export const updatePackageForSelectedCustomer = async (
	packageToUpdate: Package,
	subCustomerId: number,
	signal: AbortSignal
): Promise<string> => {
	const queryString = createQueryString({ subCustomerId });
	return appApi.ajax('put', `/api/Packages${queryString}`, packageToUpdate, signal);
};
export const getCustomWorkflowTemplates = async (
	subCustomerId: number,
	signal?: AbortSignal
): Promise<CustomWorkflowTemplate[]> => {
	const queryString = createQueryString({ subCustomerId });
	return appApi.ajax('get', `/api/WorkflowTemplates/Custom${queryString}`, null, signal);
};

export const createCustomWorkflowTemplate = async (
	customWorkflow: CustomWorkflowTemplate,
	subCustomerId: number,
	signal: AbortSignal = null
): Promise<string> => {
	const queryString = createQueryString({ subCustomerId });
	return appApi.ajax('post', `/api/WorkflowTemplates/Custom${queryString}`, customWorkflow, signal);
};

export const updateCustomWorkflowTemplate = async (
	customWorkflow: CustomWorkflowTemplate,
	subCustomerId: number,
	signal: AbortSignal = null
): Promise<string> => {
	const queryString = createQueryString({ subCustomerId });
	return appApi.ajax(
		'post',
		`/api/WorkflowTemplates/Custom/${customWorkflow.id}${queryString}`,
		customWorkflow,
		signal
	);
};

export const getWorkflowTemplate = async (
	id: string,
	subCustomerId: number,
	clientCustomerId: number = null,
	signal: AbortSignal = null
): Promise<WorkflowTemplate> => {
	const queryString = createQueryString({ subCustomerId, clientCustomerId });
	return appApi.ajax('get', `/api/WorkflowTemplates/${id}${queryString}`, null, signal);
};

export const getReminderTemplatesForCustomer = async (
	selectedCustomerId: number,
	signal: AbortSignal
): Promise<ReminderTemplate[]> => {
	const queryString = createQueryString({ selectedCustomerId });
	return appApi.ajax('get', `/api/ReminderTemplates${queryString}`, null, signal);
};

export const createFilter = async (filterSettings: FilterSettings): Promise<string> => {
	return appApi.ajax('post', '/api/filters', filterSettings);
};

export const deleteFilter = async (filterId: string): Promise<FilterSettings> =>
	appApi.ajax('delete', `/api/filters/${filterId}`);

export const getAllFilters = async (signal: AbortSignal): Promise<FilterSettings[]> =>
	appApi.ajax('get', '/api/filters', null, signal);

export const dismissChangeLog = async (workflowId: string, changeLog: ChangeLog): Promise<Workflow> =>
	appApi.ajax('post', `/api/Workflows/${workflowId}/ChangeLog`, changeLog);

export const dismissFraudFlag = async (workflowId: string, fraudFlag: FraudFlag): Promise<Workflow> =>
	appApi.ajax('post', `/api/Workflows/${workflowId}/FraudFlag`, fraudFlag);

export const markRefereeAnswer = async (
	workflowId: string,
	candidateId: string,
	refereeAnswersMarkResult: string,
	answerIndex: number
): Promise<Workflow> => {
	const queryString = createQueryString({ candidateId, refereeAnswersMarkResult, answerIndex });
	return appApi.ajax('post', `/api/Workflows/${workflowId}/RefereeAnswer${queryString}`);
};

export const requestCreditChecksByScreener = async (
	workflowId: string,
	clientCustomerId: number,
	selectedAdditionalNames: AdditionalName[],
	selectedAddresses: AddressHistory[]
): Promise<Workflow[]> => {
	const queryString = createQueryString({ workflowId, clientCustomerId });
	const data = { AdditionalNames: selectedAdditionalNames, AddressHistories: selectedAddresses };
	return appApi.ajax('post', `/api/Workflows/CreditChecks${queryString}`, data);
};

const sendRequestByWorkflowIdOrCandidateId = async <T>(
	candidateId: string,
	method: string,
	path: string,
	data?: unknown
): Promise<T> => {
	const queryString = createQueryString({ candidateId });
	return appApi.ajax(method, `${path}${queryString}`, data);
};

export const sendManualReminderEmailToCandidate = async (workflowId: string): Promise<void> => {
	return appApi.ajax('post', `/api/Workflows/${workflowId}/Reminders`);
};

export const setSectionComponentIdsToBeUsedAsReactKeys = (section: Section) => {
	let id = 0;
	section.components.forEach((c) => {
		// eslint-disable-next-line no-param-reassign
		c.id = id.toString();
		// eslint-disable-next-line no-plusplus
		id++;
	});
};

const getAuthorizationHeaderForSystemAdminMasqueradingAsReferee = (refereeToken: string) => {
	if ((hasValue(refereeToken) && hasSystemAdminRole()) || (hasValue(refereeToken) && hasCustomerSuccessRole())) {
		return `Bearer ${getAccessToken()}`;
	}
	return null;
};

export const extendWarningTimeAndExpiryTime = async (workflowId: string): Promise<Workflow[]> => {
	return appApi.ajax('post', `/api/Workflows/${workflowId}/Extend`);
};

export const getOTPCode = async (): Promise<string> => {
	return appApi.ajax('get', '/api/Authentication/otp', null, null, false);
};

export const checkOTPCode = async (email: string, password: string, OTPCode: string): Promise<AuthenticationResult> => {
	const data = { email, password, OTPCode };
	return appApi.ajax('post', '/api/Authentication/otp', data, null, false).then(
		async (value) => {
			return value;
		},
		async (err) => {
			return { errorMessage: err.body };
		}
	);
};

export const getOTPAuthUrl = async (accessToken: string): Promise<string> => {
	const customAuthorizationHeader = `Bearer ${accessToken}`;
	return await appApi.ajax('get', `/api/Users/otpurl`, null, null, false, null, customAuthorizationHeader);
};

export const scrubWorkflow = async (workflowId: string): Promise<Workflow> => {
	return appApi.ajax('post', `/api/Workflows/${workflowId}/scrub`);
};

export const getWorkflowIdsForScreeningReport = async (
	candidateId: string,
	clientCustomerId: number,
	signal: AbortSignal = null
): Promise<string[]> => {
	const queryString = createQueryString({ clientCustomerId });
	return appApi.ajax('get', `/api/Candidate/${candidateId}/Workflows${queryString}`, null, signal);
};

export const getConnectIdAuthUrl = async (serverId: string, workflowId: string): Promise<ConnectIdAuthResponse> => {
	const queryString = createQueryString({ serverId, workflowId });
	return appApi.ajax('get', `/api/ConnectId/authUrl${queryString}`);
};

export const retriveConnectIdTokenDetails = async (data: ConnectIdTokenRequestModel): Promise<any> => {
	return appApi.ajax('post', '/api/ConnectId/tokens', data, null).then(
		async (value) => {
			return value;
		},
		async (err) => {
			return { errorMessage: err.body };
		}
	);
};

export const getAllWorkflowTemplates = async (signal?: AbortSignal): Promise<WorkflowTemplate[]> => {
	return appApi.ajax('get', `/api/WorkflowTemplates/All`, null, signal);
};

export const createEventLog = async (workflowId: string, details: string) => {
	return appApi.ajax('post', `/api/Workflow/${workflowId}/eventlog`, details);
};

export const deleteNameChangeDocuments = async (workflowId: string) => {
	return appApi.ajax('delete', `/api/Workflows/${workflowId}/additionalnamedocuments`);
};

export const clearCache = async (collectionName: string) => {
	return appApi.ajax('delete', `/api/Cache/${collectionName}`);
};

// Queues a Talentia data export for a customer (and its sub-customers). The export is
// generated asynchronously; a download link is emailed to the requester when ready.
export const exportCustomerData = async (customerId: number) => {
	return appApi.ajax('get', `/api/Customers/ExportData?customerId=${customerId}`);
};

export const updateRenewalDate = async (workflowId: string, renewalDate: string) => {
	const queryString = createQueryString({ renewalDate });
	return appApi.ajax('put', `/api/Workflows/${workflowId}/renewal${queryString}`);
};

export const sendAccountSetupEmail = async (customer: Registration) => {
	return appApi.ajax('post', `/api/Customer/register`, customer, null, false);
};

export const getCustomerUsers = async (customerId: number, signal: AbortSignal = null): Promise<User[]> => {
	return appApi.ajax('get', `/api/Users/Customers/${customerId}`, null, signal);
};

export const getMitekCheckDocument = async (fileName: string): Promise<FileItem> => {
	const queryString = createQueryString({ fileName });
	return appApi.ajax('get', `/api/files${queryString}`);
};

export const unarchiveCandidate = async (candidateId: string): Promise<Candidate> => {
	return await appApi.ajax(
		'post',
		`/api/candidates/${candidateId}${createQueryString({ archive: 'false', action: 'unarchiveCandidate' })}`
	);
};

export const archiveCandidate = async (candidateId: string): Promise<Candidate> => {
	return await appApi.ajax(
		'post',
		`/api/candidates/${candidateId}${createQueryString({ archive: 'true', action: 'archiveCandidate' })}`
	);
};

export const processPayment = async (paymentData: PaymentModel) => {
	const response = appApi.ajax('post', '/api/payment', paymentData);
	return await response;
};

export const getLicencedUsers = async (customerId: number) => {
	const response = appApi.ajax('get', `/api/Customer/${customerId}/Users`);
	return await response;
};

export const getInternationalCheckDetails = async (
	workflowIds: string[],
	customerId: number
): Promise<InternationalChecks[]> => {
	const queryString = createQueryString({ workflowIds: workflowIds.join(', '), customerId });
	const response = appApi.ajax('get', `/api/workflow/country${queryString}`);
	return await response;
};

export const getBillingDetails = async (workflowId: string): Promise<BillingDetails> => {
	const response = await appApi.ajax('get', `/api/billing/${workflowId}`);
	return response;
};
